"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Loader2, FileCheck2, Clock, ListChecks, Trophy } from "lucide-react";
import { api } from "@/lib/api-client";
import { EmptyState } from "@/components/ui/EmptyState";
import { useTerm } from "@/components/tenant/TenantProvider";
import { StatusBadge } from "@/components/ui/AdminTable";

interface ExamListItem {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  exam_type: string;
  duration_minutes: number | null;
  passing_score: number | null;
  max_attempts: number | null;
  status: string;
  question_count: number;
  my_attempts: number;
  my_best_percent: number | null;
}

export default function TryoutListPage() {
  const term = useTerm();
  const [exams, setExams] = useState<ExamListItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api<ExamListItem[]>("/api/exams", { cacheTtl: 0 }).then((res) => {
      if (res.success && res.data) setExams(res.data);
      setLoading(false);
    });
  }, []);

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="mb-6">
        <h1 className="text-[1.875rem] font-black leading-none tracking-[-0.03em] text-[#111827]">{term("tryout", "Tryout")} & Ujian</h1>
        <p className="mt-1 text-sm text-[#6B7280]">Uji kemampuanmu dengan {term("tryout", "Tryout")} bergaya CBT — dinilai otomatis, lengkap dengan ranking.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-[#6B7280]">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : exams.length === 0 ? (
        <EmptyState title={`Belum ada ${term("tryout", "Tryout")}`} description={`${term("tryout", "Tryout")} yang dipublikasikan akan muncul di sini.`} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {exams.map((exam) => (
            <Link
              key={exam.id}
              href={`/tryout/${exam.slug}`}
              className="group rounded-2xl border border-black/10 bg-white p-5 shadow-sm transition hover:border-[var(--brand-primary)]/40 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]">
                  <FileCheck2 className="h-5 w-5" />
                </div>
                {exam.status === "closed" && <StatusBadge status="closed" />}
              </div>
              <h2 className="mt-3 font-semibold text-[#111827] group-hover:text-[var(--brand-primary)]">{exam.title}</h2>
              {exam.description && <p className="mt-1 line-clamp-2 text-sm text-[#6B7280]">{exam.description}</p>}
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#6B7280]">
                <span className="inline-flex items-center gap-1"><ListChecks className="h-3.5 w-3.5" /> {exam.question_count} soal</span>
                {exam.duration_minutes && (
                  <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {exam.duration_minutes} menit</span>
                )}
                {exam.my_best_percent !== null && (
                  <span className="inline-flex items-center gap-1 font-medium text-[var(--brand-primary)]">
                    <Trophy className="h-3.5 w-3.5" /> Terbaik: {exam.my_best_percent}%
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
